import type { WCollectionGroupView } from '@zola/svc-web-api-ts-client';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import Headings from '@zola/zola-ui/src/typography/Headings';
import {
  PrimaryNavIdentifier,
  SecondaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { isUserPostWedding } from '@zola-helpers/client/dist/es/util/dateUtils';
import { UserContext } from '@zola-helpers/client/dist/types/@types';

import cx from 'classnames';
import type { CamelCasedPropertiesDeep } from 'type-fest';

import MainLayout from '~/components/common/layouts/MainLayout/MainLayout';
import Banner from '~/components/shop/Banner/Banner';

import { CollectionGroupGrid } from './components/CollectionGroupGrid';

import styles from './giftCollections.module.less';

export type GiftCollectionProps = {
  collectionGroup: CamelCasedPropertiesDeep<WCollectionGroupView>;
  collectionGroups: CamelCasedPropertiesDeep<WCollectionGroupView>[];
};

const Sidebar = ({ collectionGroup, collectionGroups }: GiftCollectionProps): JSX.Element => {
  return (
    <nav className={styles.sidebar}>
      <ul>
        <li>
          <LinkV2
            href="/shop/gift-collections"
            noUnderline
            className={cx(styles.active, styles.collectionGroupType)}
          >
            Starter Collections
          </LinkV2>
        </li>
        <li className={styles.collectionGroups}>
          <ul>
            {collectionGroups.map((group) => (
              <li key={group.key}>
                <LinkV2
                  href={`/shop/gift-collections/${group.key}`}
                  noUnderline
                  className={cx({ [styles.active]: group.key === collectionGroup.key })}
                >
                  {group.name}
                </LinkV2>
              </li>
            ))}
          </ul>
        </li>
      </ul>
    </nav>
  );
};

export const GiftCollections = ({
  collectionGroup,
  collectionGroups,
  isBaby,
  userContext,
}: GiftCollectionProps & { isBaby: boolean; userContext: UserContext | null }): JSX.Element => {
  const isGuest = userContext ? Boolean(userContext.is_guest) : true;
  let adjustedActiveSecondaryLink = SecondaryNavIdentifier.COLLECTIONS;
  if (!isGuest && !isBaby) {
    adjustedActiveSecondaryLink = isUserPostWedding(userContext || undefined)
      ? SecondaryNavIdentifier.POST_WEDDING_SHOP
      : SecondaryNavIdentifier.ADD_GIFTS;
  }

  return (
    <MainLayout
      activePrimaryLink={isBaby ? PrimaryNavIdentifier.SHOP : PrimaryNavIdentifier.REGISTRY}
      activeSecondaryLink={adjustedActiveSecondaryLink}
      activeTertiaryLink={SecondaryNavIdentifier.COLLECTIONS}
      isBaby={isBaby}
    >
      <div className={styles.giftCollections}>
        <div className={styles.banner}>
          <Banner isWeddingBoutiquePage={undefined} />
        </div>
        <Headings.Title1 presentation="h3" strong>
          {collectionGroup.name}
        </Headings.Title1>
        <div className={styles.wrapper}>
          <Sidebar collectionGroup={collectionGroup} collectionGroups={collectionGroups} />
          <CollectionGroupGrid collectionGroup={collectionGroup} />
        </div>
      </div>
    </MainLayout>
  );
};
